import HighchartsPrivacyModeFilter from "components/Text/HighchartsPrivacyModeFilter";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import type React from "react";

// Defaults every chart shared by copy-paste. Layered UNDER the caller's options
// (via Highcharts.merge, last-wins) so any chart can override them.
const CHART_DEFAULTS: Highcharts.Options = {
  credits: { enabled: false },
  noData: { style: { fontWeight: "normal", fontSize: "15px", color: "black" } },
  time: { timezone: "Europe/London" },
  exporting: {
    fallbackToExportServer: false,
    buttons: {
      contextButton: {
        menuItems: [
          "viewFullscreen",
          "printChart",
          "separator",
          "downloadPNG",
          "downloadJPEG",
          "downloadSVG",
        ],
      },
    },
  },
};

/**
 * Merge the shared chart defaults under the caller's options:
 * `CHART_DEFAULTS < computed lang.noData < caller options`. Pure + testable.
 */
export function buildChartOptions(
  options: Highcharts.Options,
  opts?: { loading?: boolean; noDataText?: string },
): Highcharts.Options {
  const noData =
    opts?.noDataText ??
    (opts?.loading ? "Loading Data " : "No data for this time period");
  return Highcharts.merge(CHART_DEFAULTS, { lang: { noData } }, options);
}

interface ChartProps {
  /** Per-chart config: series, axes, chart.type, tooltips, … */
  options: Highcharts.Options;
  /** Drives the default `lang.noData` ("Loading Data " vs "No data…"). */
  loading?: boolean;
  /** Override the loading/no-data placeholder text. */
  noDataText?: string;
  /** When true, render the blur <filter> def (callers apply `filter:url(#blur)`
   * in their own tooltips). Pass your computed privacyMode. */
  privacyMode?: boolean;
  /** Passthrough to HighchartsReact. */
  immutable?: boolean;
}

/**
 * Opt-in wrapper over HighchartsReact that applies the shared Highcharts option
 * defaults and (optionally) the privacy-mode blur filter, so chart components
 * only declare what's actually specific to them.
 */
export default function Chart({
  options,
  loading,
  noDataText,
  privacyMode,
  immutable,
}: ChartProps): React.ReactElement {
  return (
    <>
      {privacyMode && <HighchartsPrivacyModeFilter />}
      <HighchartsReact
        highcharts={Highcharts}
        options={buildChartOptions(options, { loading, noDataText })}
        immutable={immutable}
      />
    </>
  );
}
